/**
 * Test suite for camera projection math
 * Run this to verify quaternion conversion and ray projection before UI testing
 */

import { quaternionToMatrix, project3DPointToCamera, isProjectedPointInCenterBounds, isInBounds } from './cameraProjection'

// Test 1: Quaternion to matrix conversion
export function testQuaternionToMatrix() {
  console.log('\n=== Test 1: Quaternion to Matrix ===')
  
  // Identity quaternion: [w=1, x=0, y=0, z=0] → identity matrix
  const qIdentity: [number, number, number, number] = [1, 0, 0, 0]
  const matIdentity = quaternionToMatrix(qIdentity)
  
  console.log('Identity quaternion [1, 0, 0, 0] should give identity matrix:')
  console.log('Row 0:', matIdentity[0].map(x => x.toFixed(4)))
  console.log('Row 1:', matIdentity[1].map(x => x.toFixed(4)))
  console.log('Row 2:', matIdentity[2].map(x => x.toFixed(4)))
  
  const isIdentity = 
    Math.abs(matIdentity[0][0] - 1) < 0.01 &&
    Math.abs(matIdentity[1][1] - 1) < 0.01 &&
    Math.abs(matIdentity[2][2] - 1) < 0.01 &&
    Math.abs(matIdentity[0][1]) < 0.01 &&
    Math.abs(matIdentity[0][2]) < 0.01
    
  console.log(`✓ Identity test: ${isIdentity ? 'PASS' : 'FAIL'}`)
  
  // 90-degree rotation around Z: [w=cos(45°), z=sin(45°)] ≈ [0.707, 0, 0, 0.707]
  const q90z: [number, number, number, number] = [0.7071, 0, 0, 0.7071]
  const mat90z = quaternionToMatrix(q90z)
  
  console.log('\n90° Z rotation [0.707, 0, 0, 0.707]:')
  console.log('Should rotate X→Y:')
  console.log('Row 0:', mat90z[0].map(x => x.toFixed(4)))
  console.log('Row 1:', mat90z[1].map(x => x.toFixed(4)))
  console.log('Row 2:', mat90z[2].map(x => x.toFixed(4)))
  
  // Real camera quaternion from cameras.json
  const qReal: [number, number, number, number] = [0.9909133501058428, 0.12018260014078806, -0.05960542459362376, 0.00969889501071766]
  const matReal = quaternionToMatrix(qReal)
  const qNorm = Math.sqrt(qReal[0]**2 + qReal[1]**2 + qReal[2]**2 + qReal[3]**2)
  
  console.log(`\nReal camera quaternion (norm: ${qNorm.toFixed(4)}):`)
  console.log('Row 0:', matReal[0].map(x => x.toFixed(4)))
  console.log('Row 1:', matReal[1].map(x => x.toFixed(4)))
  console.log('Row 2:', matReal[2].map(x => x.toFixed(4)))
}

// Test 2: Projection with known cameras
export function testProjectionSimple() {
  console.log('\n=== Test 2: Simple Projection ===')
  
  // Create camera for projection
  const cam2 = {
    position: [1, 0, 5],  // Shifted 1m right and 5m back from origin
    rotation: [1, 0, 0, 0],  // Identity rotation
    intrinsics: {
      fx: 800, fy: 800,
      cx: 225, cy: 400,
      width: 450, height: 800
    }
  }
  
  // Test projecting a 3D point to cam2
  // A point at (0, 0, 10) in world space (10m away)
  const point3D: [number, number, number] = [0, 0, 10]
  
  const projected = project3DPointToCamera(point3D, cam2)
  
  console.log(`3D point [0, 0, 10] projects to cam2 at:`)
  if (projected) {
    console.log(`  [${projected[0].toFixed(1)}, ${projected[1].toFixed(1)}]`)
    console.log(`  Image bounds: ${cam2.intrinsics.width}×${cam2.intrinsics.height}`)
    const inBounds = isInBounds(projected, cam2.intrinsics)
    console.log(`  ✓ ${inBounds ? 'In bounds' : 'Out of bounds'}`)
  } else {
    console.log('  ❌ Projection returned null (behind camera)')
  }
}

// Test 3: Bounds checking
export function testBoundsChecking() {
  console.log('\n=== Test 3: Bounds Checking ===')
  
  const intrinsics = {
    fx: 800, fy: 800,
    cx: 225, cy: 400,
    width: 450, height: 800
  }
  
  // Test points at different distances from center
  const testPoints = [
    { x: 225, y: 400, label: 'center' },
    { x: 270, y: 400, label: 'center+45 (±20% X bound)' },
    { x: 180, y: 400, label: 'center-45 (±20% X bound)' },
    { x: 225, y: 480, label: 'center+80 (±20% Y bound)' },
    { x: 225, y: 320, label: 'center-80 (±20% Y bound)' },
    { x: 300, y: 400, label: 'center+75 (outside ±20%)' },
    { x: 225, y: 500, label: 'center+100 (outside ±20%)' }
  ]
  
  for (const pt of testPoints) {
    const in20 = isProjectedPointInCenterBounds(pt.x, pt.y, intrinsics, 0.2)
    const in30 = isProjectedPointInCenterBounds(pt.x, pt.y, intrinsics, 0.3)
    const in50 = isProjectedPointInCenterBounds(pt.x, pt.y, intrinsics, 0.5)
    console.log(`  [${pt.x}, ${pt.y}] ${pt.label}: ±20%=${in20}, ±30%=${in30}, ±50%=${in50}`)
  }
}

// Run all tests
export function runAllTests() {
  console.log('🧪 Camera Projection Math Tests')
  console.log('================================')
  
  testQuaternionToMatrix()
  testProjectionSimple()
  testBoundsChecking()
  
  console.log('\n✅ Tests complete. Check results above.')
}
